// src/screens/AppTermsScreen.js
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MerchantTermsScreen.css';

import backIcon from '../assets/backspace-icon.png';

export default function AppTermsScreen() {
  const navigate = useNavigate();

  const scrollRef = useRef(null);
  const [showTopShadow, setShowTopShadow] = useState(false);

  const terms = useMemo(() => ([
    {
      title: '1) App Account Eligibility',
      bullets: [
        'Only your main BarterFi account can register a new app account.',
        'App accounts are linked sub-accounts created under the ownership of your main account.',
        'App accounts cannot register or create other app accounts.',
        'You must provide accurate app details, including app name, username, category, and domain.',
      ],
    },
    {
      title: '2) Ownership & Linked Identity',
      bullets: [
        'Every app account remains linked to the primary owner account that created it.',
        'BarterFi may create a linked internal app user identity for the app after successful domain verification and activation.',
        'The app account does not replace your real Pi identity. Ownership and responsibility remain tied to the main account.',
        'You are responsible for all activity performed through app accounts registered under your ownership.',
      ],
    },
    {
      title: '3) Domain Requirement & Verification',
      bullets: [
        'Each app account must use a real domain that you control.',
        'To verify ownership, you must place the BarterFi validation file on your domain exactly as instructed during registration.',
        'Your app account will not become active until domain verification succeeds.',
        'If the validation file is missing, empty, incorrect, or unreachable, verification may fail.',
      ],
    },
    {
      title: '4) Domain Reuse, Release, and Admin Review',
      bullets: [
        'BarterFi tracks historical domain usage to protect the platform from fake, abandoned, or misleading app registrations.',
        'If a domain was previously used by a verified and active app on BarterFi, a new registration using that same domain may require admin review before activation.',
        'This review process helps keep the platform clean and prevents abuse through recycled or suspicious app identities.',
        'If an app is closed, the live domain may be released for future reuse, but the historical record of that app can still be preserved by BarterFi.',
      ],
    },
    {
      title: '5) App Lifecycle & Deactivation',
      bullets: [
        'New app accounts normally begin in draft status until domain verification is completed.',
        'Verified apps may become active immediately or may move into pending review depending on domain history and platform rules.',
        'BarterFi may suspend, restrict, or reject app accounts that violate platform rules or create trust and safety concerns.',
        'Closing an app account does not erase all historical interactions. BarterFi may preserve records needed for platform integrity, moderation, or audits.',
      ],
    },
    {
      title: '6) App Wallets & Pi Deposits',
      bullets: [
        'An app wallet can only be enabled after the owner’s main account already has an activated BarterFi wallet.',
        'App accounts do not act as separate Pi Browser identities.',
        'When making a Pi deposit, Pi SDK approval is always performed using the real Pi account currently authenticated in Pi Browser.',
        'The selected BarterFi identity at the time of deposit determines which internal wallet balance gets credited.',
        'This means your real Pi account can fund either your main account wallet or one of your linked app account wallets, depending on which BarterFi identity is active when the deposit starts.',
      ],
    },
    {
      title: '7) Pi Identity & Responsibility',
      bullets: [
        'Because Pi SDK payment approval is tied to your real Pi Browser session, you must understand that app wallet funding still depends on your real Pi account.',
        'You should not assume an app account has its own independent Pi wallet identity unless BarterFi explicitly supports that in the future.',
        'You are responsible for ensuring you are logged into the correct BarterFi account before approving any Pi payment or deposit flow.',
      ],
    },
    {
      title: '8) Prohibited App Behavior',
      bullets: [
        'Creating fake, misleading, impersonating, or inactive app identities.',
        'Using domains you do not control or attempting to bypass domain verification.',
        'Trying to create app accounts from app accounts.',
        'Using app identities to scam users, misrepresent services, or abuse the Pi ecosystem.',
        'Repeatedly creating, abandoning, recycling, or manipulating app accounts in ways that harm platform trust.',
      ],
    },
    {
      title: '9) Platform Rights & Enforcement',
      bullets: [
        'BarterFi reserves the right to review, suspend, deactivate, reject, or remove app accounts when necessary to protect users and the ecosystem.',
        'BarterFi may require additional review for domains, app identities, or suspicious registration activity.',
        'BarterFi may update these terms over time as app account features evolve.',
        'Continued use of app account features means you agree to follow the latest version of these terms.',
      ],
    },
    {
      title: '10) Acceptance',
      bullets: [
        'By registering an app account, you confirm that you have read and understood these App Account Terms and Responsibilities.',
        'You also confirm that you understand the link between your main account, your real Pi identity, your verified domain, and any linked app accounts you create on BarterFi.',
      ],
    },
  ]), []);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowTopShadow(el.scrollTop > 6);
  };

  return (
    <div className="merchant-terms-screen">
      <div className={`merchant-terms-topbar ${showTopShadow ? 'shadow' : ''}`}>
        <button
          className="mts-back"
          type="button"
          onClick={() => navigate(-1)}
        >
          <img src={backIcon} alt="Back" />
        </button>

        <div className="mts-title">App Account Terms</div>
        <div className="mts-right" />
      </div>

      <div
        className="merchant-terms-body"
        ref={scrollRef}
        onScroll={onScroll}
      >
        <p className="mts-intro">
          Please read these terms carefully before registering an app account.
          These rules explain how app ownership, domain verification, linked app identities,
          wallet activation, and Pi deposits work on BarterFi.
        </p>

        {terms.map((sec) => (
          <div key={sec.title} className="mts-section">
            <div className="mts-h">{sec.title}</div>
            <ul className="mts-list">
              {sec.bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>
        ))}

        <div className="mts-bottom-spacer" />
      </div>
    </div>
  );
}
